import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, MapPin, Calendar, Sun, Thermometer, Umbrella, Star, Building2, Briefcase, Wallet, Users, Plane, Clock, Globe } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { HotelCard, Hotel } from '../components/HotelCard';
// Destination data
const destinations: Record<string, {
  name: string;
  country: string;
  heroImage: string;
  description: string;
  highlights: string[];
  bestTime: {
    peak: string;
    shoulder: string;
    offPeak: string;
    weather: {
      month: string;
      temp: string;
      rainfall: string;
    }[];
  };
  hotels: Hotel[];
  faqs: {
    question: string;
    answer: string;
  }[];
}> = {
  dubai: {
    name: 'Dubai',
    country: 'United Arab Emirates',
    heroImage: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1600&q=80',
    description: "Dubai is a city of superlatives—home to the world's tallest building, largest shopping mall, and most luxurious hotels. This gleaming metropolis in the desert offers an extraordinary blend of traditional Arabian culture and futuristic innovation. From the historic Al Fahidi district to the man-made Palm Jumeirah island, Dubai promises unforgettable experiences for every traveler.",
    highlights: ["Burj Khalifa - World's tallest building", 'Palm Jumeirah - Iconic man-made island', "Dubai Mall - World's largest shopping destination", 'Desert Safari - Traditional Arabian adventure', 'Gold Souk - Traditional marketplace', 'Dubai Marina - Stunning waterfront district'],
    bestTime: {
      peak: 'November to March - Perfect weather with temperatures between 20-30°C. Ideal for outdoor activities and beach visits.',
      shoulder: 'April and October - Warm but manageable. Fewer crowds and better hotel rates.',
      offPeak: 'May to September - Hot summer months (40°C+). Best for indoor attractions and significant discounts.',
      weather: [{
        month: 'Nov-Feb',
        temp: '20-25°C',
        rainfall: 'Minimal'
      }, {
        month: 'Mar-Apr',
        temp: '25-32°C',
        rainfall: 'Rare'
      }, {
        month: 'May-Sep',
        temp: '35-45°C',
        rainfall: 'None'
      }, {
        month: 'Oct',
        temp: '30-35°C',
        rainfall: 'Rare'
      }]
    },
    hotels: [
    // 5-Star Luxury
    {
      id: 'd1',
      name: 'Burj Al Arab Jumeirah',
      location: 'Jumeirah Beach, Dubai',
      rating: 4.9,
      reviews: 2847,
      price: 1850,
      image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
      offer: {
        text: 'Iconic Stay',
        type: 'deal'
      },
      amenities: ['Butler Service', 'Private Beach', 'Spa', 'Fine Dining', 'Helipad']
    }, {
      id: 'd2',
      name: 'Atlantis The Royal',
      location: 'Palm Jumeirah, Dubai',
      rating: 4.9,
      reviews: 1523,
      price: 1200,
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80',
      offer: {
        text: 'New Opening',
        type: 'limited'
      },
      amenities: ['Infinity Pool', 'Aquarium', 'Celebrity Restaurants', 'Spa', 'Beach Club']
    }, {
      id: 'd3',
      name: 'One&Only The Palm',
      location: 'Palm Jumeirah, Dubai',
      rating: 4.8,
      reviews: 1876,
      price: 980,
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
      amenities: ['Private Beach', 'Spa', 'Pool', 'Fine Dining', 'Water Sports']
    },
    // 4-Star Business
    {
      id: 'd4',
      name: 'JW Marriott Marquis',
      location: 'Business Bay, Dubai',
      rating: 4.6,
      reviews: 3421,
      price: 320,
      image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80',
      offer: {
        text: '20% OFF',
        type: 'discount'
      },
      amenities: ['Business Center', 'Pool', 'Spa', 'Multiple Restaurants', 'Gym']
    }, {
      id: 'd5',
      name: 'Sheraton Grand Dubai',
      location: 'Sheikh Zayed Road, Dubai',
      rating: 4.5,
      reviews: 2156,
      price: 280,
      image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
      amenities: ['Business Center', 'Pool', 'Restaurant', 'Gym', 'Meeting Rooms']
    }, {
      id: 'd6',
      name: 'Hyatt Regency Dubai',
      location: 'Deira, Dubai',
      rating: 4.4,
      reviews: 1892,
      price: 220,
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80',
      amenities: ['Golf Course', 'Pool', 'Spa', 'Restaurant', 'Tennis']
    },
    // Budget Friendly
    {
      id: 'd7',
      name: 'Rove Downtown',
      location: 'Downtown Dubai',
      rating: 4.3,
      reviews: 4521,
      price: 120,
      image: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80',
      offer: {
        text: 'Best Value',
        type: 'deal'
      },
      amenities: ['Free WiFi', 'Pool', 'Gym', 'Restaurant', 'Burj View']
    }, {
      id: 'd8',
      name: 'Premier Inn Dubai',
      location: 'Al Barsha, Dubai',
      rating: 4.2,
      reviews: 3876,
      price: 85,
      image: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800&q=80',
      amenities: ['Free WiFi', 'Restaurant', 'Gym', 'Parking', 'Metro Access']
    },
    // Family Hotels
    {
      id: 'd9',
      name: 'Atlantis The Palm',
      location: 'Palm Jumeirah, Dubai',
      rating: 4.7,
      reviews: 5632,
      price: 650,
      image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
      offer: {
        text: 'Kids Free',
        type: 'deal'
      },
      amenities: ['Waterpark', 'Aquarium', 'Kids Club', 'Beach', 'Multiple Pools']
    }],
    faqs: [{
      question: 'What is the best area to stay in Dubai?',
      answer: 'It depends on your preferences. Downtown Dubai is ideal for sightseeing near Burj Khalifa and Dubai Mall. Palm Jumeirah and JBR offer beach resort experiences. Business Bay suits business travelers, while Deira provides a more traditional, budget-friendly experience.'
    }, {
      question: 'Do I need a visa to visit Dubai?',
      answer: "Many nationalities receive visa-on-arrival for 30-90 days. Citizens of GCC countries don't need a visa. Check with UAE immigration for your specific nationality. Visa requirements can change, so verify before traveling."
    }, {
      question: 'Is Dubai expensive for tourists?',
      answer: 'Dubai offers options for all budgets. Luxury hotels can cost $500-2000+/night, but budget options exist from $50-150/night. Street food and local restaurants are affordable, while fine dining is premium. Many attractions have free or low-cost options.'
    }, {
      question: 'What is the dress code in Dubai?',
      answer: 'Dubai is relatively liberal but respectful dress is appreciated. Beachwear is fine at pools and beaches. In malls and public areas, shoulders and knees should be covered. Mosques require modest dress with head coverings for women.'
    }, {
      question: 'How do I get around Dubai?',
      answer: 'Dubai has excellent public transport including the Metro, buses, and water taxis. Taxis and ride-sharing (Uber, Careem) are widely available. Many tourists rent cars, though traffic can be heavy. The Metro connects major tourist areas efficiently.'
    }, {
      question: 'Is tap water safe to drink in Dubai?',
      answer: "Tap water in Dubai is technically safe but most people prefer bottled water due to taste. Hotels provide bottled water, and it's inexpensive to buy. All restaurants use filtered or bottled water for cooking and serving."
    }]
  },
  kathmandu: {
    name: 'Kathmandu',
    country: 'Nepal',
    heroImage: 'https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=1600&q=80',
    description: 'Kathmandu, the capital of Nepal, is a city where ancient temples stand alongside bustling markets, and the majestic Himalayas form a stunning backdrop. This UNESCO World Heritage city is the gateway to Everest and offers a unique blend of Hindu and Buddhist culture, incredible trekking opportunities, and warm Nepali hospitality.',
    highlights: ['Boudhanath Stupa - One of the largest stupas in the world', 'Pashupatinath Temple - Sacred Hindu temple', 'Durbar Square - Ancient royal palace complex', 'Swayambhunath - The Monkey Temple', 'Thamel - Vibrant tourist district', 'Gateway to Everest Base Camp'],
    bestTime: {
      peak: 'October to November - Clear skies, perfect for trekking and mountain views. Pleasant temperatures around 20°C.',
      shoulder: 'March to April - Spring bloثoms, rhododendrons in bloom. Good for trekking with occasional rain.',
      offPeak: 'June to September - Monsoon season with heavy rainfall. Fewer tourists but lush green landscapes.',
      weather: [{
        month: 'Oct-Nov',
        temp: '15-25°C',
        rainfall: 'Low'
      }, {
        month: 'Dec-Feb',
        temp: '5-15°C',
        rainfall: 'Minimal'
      }, {
        month: 'Mar-May',
        temp: '15-28°C',
        rainfall: 'Moderate'
      }, {
        month: 'Jun-Sep',
        temp: '20-30°C',
        rainfall: 'Heavy'
      }]
    },
    hotels: [{
      id: 'k1',
      name: "Dwarika's Hotel",
      location: 'Battisputali, Kathmandu',
      rating: 4.9,
      reviews: 1256,
      price: 320,
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
      offer: {
        text: 'Heritage Stay',
        type: 'deal'
      },
      amenities: ['Spa', 'Restaurant', 'Garden', 'Cultural Tours', 'Yoga']
    }, {
      id: 'k2',
      name: 'Hyatt Regency Kathmandu',
      location: 'Boudha, Kathmandu',
      rating: 4.7,
      reviews: 2134,
      price: 180,
      image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80',
      amenities: ['Pool', 'Spa', 'Multiple Restaurants', 'Gym', 'Garden']
    }, {
      id: 'k3',
      name: 'Hotel Yak & Yeti',
      location: 'Durbar Marg, Kathmandu',
      rating: 4.5,
      reviews: 987,
      price: 150,
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80',
      amenities: ['Casino', 'Pool', 'Spa', 'Multiple Restaurants', 'Bar']
    }, {
      id: 'k4',
      name: 'Kathmandu Marriott Hotel',
      location: 'Naxal, Kathmandu',
      rating: 4.6,
      reviews: 756,
      price: 160,
      image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
      offer: {
        text: '15% OFF',
        type: 'discount'
      },
      amenities: ['Pool', 'Spa', 'Restaurant', 'Gym', 'Business Center']
    }, {
      id: 'k5',
      name: 'Hotel Shanker',
      location: 'Lazimpat, Kathmandu',
      rating: 4.3,
      reviews: 1432,
      price: 85,
      image: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80',
      amenities: ['Garden', 'Restaurant', 'Bar', 'Heritage Building', 'WiFi']
    }, {
      id: 'k6',
      name: 'Thamel Eco Resort',
      location: 'Thamel, Kathmandu',
      rating: 4.2,
      reviews: 2341,
      price: 45,
      image: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800&q=80',
      offer: {
        text: 'Budget Pick',
        type: 'deal'
      },
      amenities: ['Rooftop', 'Restaurant', 'WiFi', 'Tour Desk', 'Laundry']
    }],
    faqs: [{
      question: 'Do I need a visa for Nepal?',
      answer: "Most nationalities can get a visa on arrival at Tribhuvan International Airport. Tourist visas are available for 15, 30, or 90 days. Indian nationals don't need a visa. Bring passport photos and USD for the fee."
    }, {
      question: 'Is Kathmandu safe for tourists?',
      answer: 'Kathmandu is generally safe for tourists. Petty theft can occur in crowded areas, so keep valuables secure. The locals are friendly and helpful. Political demonstrations occasionally occur but rarely affect tourists.'
    }, {
      question: 'What currency is used in Nepal?',
      answer: 'The Nepalese Rupee (NPR) is the local currency. USD and Indian Rupees are widely accepted for exchange. ATMs are available in Thamel and major areas. Credit cards are accepted at larger hotels and restaurants.'
    }, {
      question: 'How do I get from the airport to my hotel?',
      answer: 'Pre-arranged hotel pickup is recommended. Taxis are available outside the terminal—negotiate the fare before getting in (around NPR 700-1000 to Thamel). Ride-sharing apps like Pathao also operate in Kathmandu.'
    }]
  }
};
// Category definitions
const categories = [{
  id: 'luxury',
  name: 'Luxury',
  icon: Star,
  description: '5-star premium experiences'
}, {
  id: 'business',
  name: 'Business',
  icon: Briefcase,
  description: 'Perfect for work travel'
}, {
  id: 'budget',
  name: 'Budget',
  icon: Wallet,
  description: 'Great value options'
}, {
  id: 'family',
  name: 'Family',
  icon: Users,
  description: 'Kid-friendly amenities'
}];
interface DestinationPageProps {
  destinationId?: string;
}
export function DestinationPage({
  destinationId = 'dubai'
}: DestinationPageProps) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const destination = destinations[destinationId] || destinations.dubai;
  // Filter hotels by star rating
  const fiveStarHotels = destination.hotels.filter(h => h.rating >= 4.8);
  const fourStarHotels = destination.hotels.filter(h => h.rating >= 4.4 && h.rating < 4.8);
  const budgetHotels = destination.hotels.filter(h => h.price < 150);
  // Filter hotels by category
  const getHotelsByCategory = (category: string) => {
    switch (category) {
      case 'luxury':
        return destination.hotels.filter(h => h.price >= 500);
      case 'business':
        return destination.hotels.filter(h => h.amenities.some(a => a.toLowerCase().includes('business')));
      case 'budget':
        return destination.hotels.filter(h => h.price < 200);
      case 'family':
        return destination.hotels.filter(h => h.amenities.some(a => a.toLowerCase().includes('kids') || a.toLowerCase().includes('family') || a.toLowerCase().includes('waterpark')));
      default:
        return destination.hotels;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-456">
      <Header data-id="element-457" />

      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden" data-id="element-458">
        <img src={destination.heroImage} alt={destination.name} className="absolute inset-0 w-full h-full object-cover" data-id="element-459" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-460" />

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12" data-id="element-461">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-white/80 text-sm mb-4" data-id="element-462">
            <a href="#" onClick={() => window.location.hash = ''} className="hover:text-white transition-colors" data-id="element-463">
              Home
            </a>
            <ChevronRight className="w-4 h-4" data-id="element-464" />
            <span data-id="element-465">Destinations</span>
            <ChevronRight className="w-4 h-4" data-id="element-466" />
            <span className="text-white font-medium" data-id="element-467">{destination.name}</span>
          </nav>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-468">
            <div className="flex items-center gap-2 text-travel-turquoise-400 mb-2" data-id="element-469">
              <MapPin className="w-5 h-5" data-id="element-470" />
              <span className="font-medium" data-id="element-471">{destination.country}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-id="element-472">
              Hotels in {destination.name}
            </h1>
            <p className="text-white/80 text-lg max-w-2xl" data-id="element-473">
              Discover {destination.hotels.length}+ handpicked hotels for your
              perfect stay
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-white border-b border-gray-100" data-id="element-474">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-475">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-476">
            {[{
            icon: Building2,
            label: 'Hotels',
            value: `${destination.hotels.length}+`
          }, {
            icon: Star,
            label: 'Avg Rating',
            value: '4.6'
          }, {
            icon: Plane,
            label: 'Flight Time',
            value: destinationId === 'dubai' ? '7h from UK' : '10h from UK'
          }, {
            icon: Clock,
            label: 'Time Zone',
            value: destinationId === 'dubai' ? 'GMT+4' : 'GMT+5:45'
          }].map(stat => <div key={stat.label} className="flex items-center gap-3" data-id="element-477">
                <div className="p-3 bg-travel-blue-50 rounded-xl" data-id="element-478">
                  <stat.icon className="w-5 h-5 text-travel-blue-600" data-id="element-479" />
                </div>
                <div data-id="element-480">
                  <p className="text-sm text-gray-500" data-id="element-481">{stat.label}</p>
                  <p className="font-bold text-gray-900" data-id="element-482">{stat.value}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Hotels by Star Rating */}
      <section className="py-12 lg:py-16" data-id="element-483">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-484">
          {/* 5-Star Hotels */}
          {fiveStarHotels.length > 0 && <div className="mb-16" data-id="element-485">
              <div className="flex items-center gap-3 mb-8" data-id="element-486">
                <div className="flex" data-id="element-487">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" data-id="element-488" />)}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-489">
                  5-Star Luxury Hotels
                </h2>
              </div>
              <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-490">
                {fiveStarHotels.map(hotel => <motion.div key={hotel.id} variants={itemVariants} data-id="element-491">
                    <HotelCard hotel={hotel} data-id="element-492" />
                  </motion.div>)}
              </motion.div>
            </div>}

          {/* 4-Star Hotels */}
          {fourStarHotels.length > 0 && <div className="mb-16" data-id="element-493">
              <div className="flex items-center gap-3 mb-8" data-id="element-494">
                <div className="flex" data-id="element-495">
                  {[...Array(4)].map((_, i) => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" data-id="element-496" />)}
                  <Star className="w-5 h-5 text-gray-300" data-id="element-497" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-498">
                  4-Star Hotels
                </h2>
              </div>
              <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-499">
                {fourStarHotels.map(hotel => <motion.div key={hotel.id} variants={itemVariants} data-id="element-500">
                    <HotelCard hotel={hotel} data-id="element-501" />
                  </motion.div>)}
              </motion.div>
            </div>}

          {/* Budget Hotels */}
          {budgetHotels.length > 0 && <div data-id="element-502">
              <div className="flex items-center gap-3 mb-8" data-id="element-503">
                <div className="p-2 bg-green-100 rounded-lg" data-id="element-504">
                  <Wallet className="w-5 h-5 text-green-600" data-id="element-505" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-506">
                  Budget-Friendly Options
                </h2>
              </div>
              <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-507">
                {budgetHotels.map(hotel => <motion.div key={hotel.id} variants={itemVariants} data-id="element-508">
                    <HotelCard hotel={hotel} data-id="element-509" />
                  </motion.div>)}
              </motion.div>
            </div>}
        </div>
      </section>

      {/* Hotels by Category */}
      <section className="py-12 lg:py-16 bg-white" data-id="element-510">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-511">
          <div className="text-center mb-10" data-id="element-512">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-513">
              Browse by Category
            </h2>
            <div className="w-20 h-1 bg-travel-turquoise-500 mx-auto rounded-full" data-id="element-514" />
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-515">
            <button onClick={() => setActiveCategory('all')} className={`px-5 py-3 rounded-xl font-medium transition-all ${activeCategory === 'all' ? 'bg-travel-blue-600 text-white shadow-lg shadow-travel-blue-600/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-516">
              All Hotels
            </button>
            {categories.map(cat => <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${activeCategory === cat.id ? 'bg-travel-blue-600 text-white shadow-lg shadow-travel-blue-600/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-517">
                <cat.icon className="w-4 h-4" data-id="element-518" />
                {cat.name}
              </button>)}
          </div>

          {/* Hotels Grid */}
          <AnimatePresence mode="wait" data-id="element-519">
            <motion.div key={activeCategory} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-520">
              {getHotelsByCategory(activeCategory).map(hotel => <HotelCard key={hotel.id} hotel={hotel} data-id="element-521" />)}
            </motion.div>
          </AnimatePresence>

          {getHotelsByCategory(activeCategory).length === 0 && <div className="text-center py-12" data-id="element-522">
              <p className="text-gray-500" data-id="element-523">
                No hotels found in this category. Try another filter.
              </p>
            </div>}
        </div>
      </section>

      {/* About Destination */}
      <section className="py-12 lg:py-16 bg-travel-blue-50" data-id="element-524">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-525">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-526">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-527">
              <div className="flex items-center gap-2 text-travel-blue-600 mb-4" data-id="element-528">
                <Globe className="w-5 h-5" data-id="element-529" />
                <span className="font-semibold uppercase tracking-wider text-sm" data-id="element-530">
                  About
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-531">
                Discover {destination.name}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8" data-id="element-532">
                {destination.description}
              </p>

              <h3 className="font-bold text-gray-900 mb-4" data-id="element-533">Top Highlights</h3>
              <ul className="grid sm:grid-cols-2 gap-3" data-id="element-534">
                {destination.highlights.map((highlight, index) => <li key={index} className="flex items-start gap-2" data-id="element-535">
                    <div className="w-5 h-5 rounded-full bg-travel-turquoise-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-536">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-537">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-538" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm" data-id="element-539">{highlight}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-540">
              <img src={destination.heroImage} alt={destination.name} className="rounded-2xl shadow-2xl" data-id="element-541" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4" data-id="element-542">
                <div className="flex items-center gap-3" data-id="element-543">
                  <div className="p-3 bg-travel-blue-100 rounded-lg" data-id="element-544">
                    <MapPin className="w-6 h-6 text-travel-blue-600" data-id="element-545" />
                  </div>
                  <div data-id="element-546">
                    <p className="font-bold text-gray-900" data-id="element-547">
                      {destination.name}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-548">
                      {destination.country}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Best Time to Visit */}
      <section className="py-12 lg:py-16 bg-white" data-id="element-549">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-550">
          <div className="text-center mb-10" data-id="element-551">
            <div className="flex items-center justify-center gap-2 text-travel-blue-600 mb-4" data-id="element-552">
              <Calendar className="w-5 h-5" data-id="element-553" />
              <span className="font-semibold uppercase tracking-wider text-sm" data-id="element-554">
                Planning
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-555">
              Best Time to Visit {destination.name}
            </h2>
            <div className="w-20 h-1 bg-travel-blue-600 mx-auto rounded-full" data-id="element-556" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-557">
            {[{
            title: 'Peak Season',
            icon: Sun,
            color: 'amber',
            content: destination.bestTime.peak
          }, {
            title: 'Shoulder Season',
            icon: Thermometer,
            color: 'blue',
            content: destination.bestTime.shoulder
          }, {
            title: 'Off-Peak Season',
            icon: Umbrella,
            color: 'green',
            content: destination.bestTime.offPeak
          }].map(season => <motion.div key={season.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-gray-50 rounded-2xl p-6" data-id="element-558">
                <div className={`inline-flex p-3 rounded-xl mb-4 ${season.color === 'amber' ? 'bg-amber-100' : season.color === 'blue' ? 'bg-blue-100' : 'bg-green-100'}`} data-id="element-559">
                  <season.icon className={`w-6 h-6 ${season.color === 'amber' ? 'text-amber-600' : season.color === 'blue' ? 'text-blue-600' : 'text-green-600'}`} data-id="element-560" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-561">{season.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-562">
                  {season.content}
                </p>
              </motion.div>)}
          </div>

          {/* Weather Table */}
          <div className="bg-gray-50 rounded-2xl overflow-hidden" data-id="element-563">
            <div className="p-6 border-b border-gray-200" data-id="element-564">
              <h3 className="font-bold text-gray-900" data-id="element-565">Weather Overview</h3>
            </div>
            <div className="overflow-x-auto" data-id="element-566">
              <table className="w-full" data-id="element-567">
                <thead className="bg-gray-100" data-id="element-568">
                  <tr data-id="element-569">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900" data-id="element-570">
                      Period
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900" data-id="element-571">
                      Temperature
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900" data-id="element-572">
                      Rainfall
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-573">
                  {destination.bestTime.weather.map((row, index) => <tr key={index} data-id="element-574">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium" data-id="element-575">
                        {row.month}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-576">
                        {row.temp}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-577">
                        {row.rainfall}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 lg:py-16 bg-gray-50" data-id="element-578">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-579">
          <div className="text-center mb-10" data-id="element-580">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-581">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600" data-id="element-582">
              Everything you need to know about visiting {destination.name}
            </p>
          </div>

          <div className="space-y-4" data-id="element-583">
            {destination.faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl overflow-hidden shadow-sm" data-id="element-584">
                <button onClick={() => setExpandedFaq(expandedFaq === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left" data-id="element-585">
                  <span className="font-semibold text-gray-900 pr-4" data-id="element-586">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: expandedFaq === index ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="flex-shrink-0" data-id="element-587">
                    <ChevronDown className="w-5 h-5 text-gray-500" data-id="element-588" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-589">
                  {expandedFaq === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-590">
                      <div className="px-6 pb-5 text-gray-600 leading-relaxed" data-id="element-591">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-592" />
    </div>;
}